import { useState } from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { showEdgeSpaces } from "@/lib/format";

/**
 * Chip de un término del perfil (recurso o ítem), con su rubro opcional y el
 * botón para quitarlo. Si recibe `onRename`, el texto se edita en el sitio.
 */
export function TermChip({
  term,
  tag,
  active = true,
  onRemove,
  onRename,
  onToggle,
}: {
  term: string;
  tag?: string | null;
  active?: boolean;
  onRemove: () => void;
  onRename?: (next: string) => void;
  onToggle?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(term);

  function startEditing() {
    setDraft(term);
    setEditing(true);
  }

  // El término se guarda tal cual: los espacios de borde son intencionales
  // (ej. "NiFF " no debe matchear "NIFFX").
  function commit() {
    setEditing(false);
    if (!draft.trim() || draft === term) return;
    onRename?.(draft);
  }

  return (
    <span
      className={cn(
        "inline-flex h-7 flex-none items-center gap-1 whitespace-nowrap rounded-md border text-xs font-semibold transition-colors",
        active
          ? "border-blue-200 bg-blue-50/70 text-blue-900 hover:border-blue-300 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-200 dark:hover:border-blue-800"
          : "border-border bg-muted/50 text-muted-foreground line-through",
        editing ? "border-blue-500 pl-1.5 pr-1 ring-2 ring-blue-500/20" : "pl-2.5 pr-1",
      )}
    >
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              // Sin frenarlo aquí, el Escape llega al Dialog y cierra el modal
              // entero en vez de solo cancelar la edición del chip.
              e.preventDefault();
              e.stopPropagation();
              setEditing(false);
            }
          }}
          style={{ width: `${Math.max(6, draft.length + 1)}ch` }}
          className="min-w-0 border-0 bg-transparent px-1 text-xs font-semibold outline-none"
        />
      ) : onRename ? (
        <button
          type="button"
          onClick={startEditing}
          title="Clic para corregir"
          className="cursor-text border-0 bg-transparent text-xs font-semibold text-inherit"
        >
          {showEdgeSpaces(term)}
        </button>
      ) : onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          title={active ? "Clic para desactivar" : "Clic para activar"}
          className="cursor-pointer border-0 bg-transparent text-xs font-semibold text-inherit"
        >
          {showEdgeSpaces(term)}
        </button>
      ) : (
        showEdgeSpaces(term)
      )}

      {tag && (
        <span
          className={cn(
            "rounded px-1.5 py-0.5 text-[10px] font-semibold",
            active
              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300"
              : "bg-muted text-muted-foreground",
          )}
        >
          {tag}
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Quitar ${term}`}
        title="Quitar"
        className={cn(
          "flex size-4 cursor-pointer items-center justify-center rounded transition-colors hover:bg-destructive/10 hover:text-destructive",
          active ? "text-blue-400 dark:text-blue-600" : "text-muted-foreground",
        )}
      >
        <X className="size-3" />
      </button>
    </span>
  );
}
